// Bir personel sekmesinin yükleniyor / erişim yok / hata / boş hâlleri.
//
// Sekmeler ayrı modüllerin uçlarını okur ve her uç kendi kapısını uygular: bir
// yönetici astının izinlerini okuyabilir ama bordro ana verisini okuyamaz. O
// yüzden 403 ve 404 bu sayfada bir arıza değil, "bu bölüm sana kapalı" demektir
// ve hata sayfası yerine nötr bir boş hâl çizilir. 404 de aynı yere düşer:
// belge ve zimmet uçları görünmeyen satırı varlığını sızdırmamak için 404 ile
// reddeder.

import { useTranslation } from "react-i18next";

import { Empty, ErrorState, Loading } from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";

export function isNoAccess(error: unknown): boolean {
  return (
    error instanceof EnvelopeError && (error.httpStatus === 403 || error.httpStatus === 404)
  );
}

export function NoAccessState(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Empty
      title={t("people.detail.tabs.no_access_title")}
      description={t("people.detail.tabs.no_access_description")}
    />
  );
}

export interface TabQueryLike {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
}

/**
 * Renders the shared non-data states of one tab section, or `children` once the
 * data is there. `empty` wins over `children` only when the read succeeded.
 */
export function TabQueryState({
  query,
  isEmpty,
  emptyTitle,
  emptyDescription,
  children,
}: {
  readonly query: TabQueryLike;
  readonly isEmpty: boolean;
  readonly emptyTitle: string;
  readonly emptyDescription?: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  if (query.isLoading) return <Loading label={t("people.detail.tabs.loading")} rows={3} />;
  if (query.isError) {
    if (isNoAccess(query.error)) return <NoAccessState />;
    return (
      <ErrorState
        title={t("people.detail.tabs.error_title")}
        description={errorMessage(query.error, t)}
      />
    );
  }
  if (isEmpty) {
    return (
      <Empty
        title={emptyTitle}
        {...(emptyDescription !== undefined && { description: emptyDescription })}
      />
    );
  }
  return <>{children}</>;
}
